/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { ChevronLeft, LogOut, Menu, X } from 'lucide-preact';
import { useEffect, useErrorBoundary, useState } from 'preact/hooks';

import useContext from '@/client/context';
import { useToast } from '@/client/components/ui/Toast';
import RouterComponent from '@client/services/router/components/router';
import '@/client/assets/tailwind.css';
import '@/client/pages/_layout/index.less';
import { OPS_SHELL_NAVIGATION, resolveOpsShellArea } from '@/client/catalogs/automation/opsShellNavigation';

import AutomationNavigationMenu from './NavigationMenu';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function SharedOpsLayout() {
    const { Router, app, page } = useContext();
    const toast = useToast();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const readPathname = () =>
        page?.context?.request.path ??
        (typeof window === 'undefined' ? Router.context?.request.path ?? '/admin/automation' : window.location.pathname);
    const [pathname, setPathname] = useState(readPathname);
    const area = resolveOpsShellArea(pathname);
    const areaConfig = OPS_SHELL_NAVIGATION[area];

    useErrorBoundary((error) => {
        app.reportBug({ before: 'Render ops layout', stacktrace: error.stack }).then(() => {
            if (__DEV__) {
                toast.error(`Ops layout error: ${error.message}`);
                return;
            }
            window.location.reload();
        });
    });

    const registerNavigationItem = (id: string, label: string) => {
        void id;
        void label;
    };

    const logout = () => {
        window.location.href = '/auth/logout';
    };

    useEffect(() => {
        const syncShellState = () => {
            setPathname(readPathname());
        };

        syncShellState();
        return Router.on('page.changed', () => syncShellState());
    }, [Router, page]);

    return (
        <div className="min-h-screen bg-gradient-background">
            <div
                className="fixed left-0 top-0 z-40 hidden h-full w-[90px] bg-white shadow-lg transition-all duration-300 md:block"
                data-testid="ops-shell-sidebar"
            >
                <div className="flex h-16 items-center justify-center bg-gradient-primary px-4">
                    <img
                        src="https://sitlmwrkxbvpqzfegvww.supabase.co/storage/v1/object/public/static/logoCPblanc.png"
                        alt="CrossPath Logo"
                        className="h-8 w-8"
                    />
                </div>

                <AutomationNavigationMenu
                    area={area}
                    pathname={pathname}
                    sidebarCollapsed={true}
                    registerNavigationItem={registerNavigationItem}
                />
            </div>

            <div className="transition-all duration-300 md:ml-[90px]">
                <RouterComponent service={Router} />
            </div>

            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden" data-testid="ops-shell-mobile-drawer">
                    <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
                    <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
                        <div className="flex h-16 items-center justify-between bg-gradient-primary px-4">
                            <div className="flex items-center gap-3">
                                <img
                                    src="https://sitlmwrkxbvpqzfegvww.supabase.co/storage/v1/object/public/static/logoCPblanc.png"
                                    alt="CrossPath Logo"
                                    className="h-8 w-8"
                                />
                                <div>
                                    <div className="text-lg font-bold text-white" data-testid="nav-brand-label">
                                        CrossPath
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-white/70" data-testid="ops-shell-subtitle">
                                        {areaConfig.subtitle}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="rounded-lg p-2 text-white transition-colors hover:bg-white/10"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <AutomationNavigationMenu
                            area={area}
                            pathname={pathname}
                            sidebarCollapsed={false}
                            registerNavigationItem={registerNavigationItem}
                            variant="mobile"
                            onNavigate={() => setMobileMenuOpen(false)}
                        />
                        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-200 p-4">
                            <button
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    logout();
                                }}
                                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700 transition-all hover:bg-red-50 hover:text-red-600"
                            >
                                <LogOut size={18} />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <button
                onClick={() => setMobileMenuOpen(true)}
                className="fixed bottom-4 left-4 z-30 rounded-full bg-gradient-primary p-3 text-white shadow-xl md:hidden"
                aria-label="Open navigation"
                data-testid="ops-shell-open-mobile"
            >
                <Menu size={20} />
            </button>
        </div>
    );
}
